import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import type { TableStatusDef, TableStatusDefInput, SyncedAskNextReason } from '@/types/api';
import { Plus, Edit2, Trash2, X } from 'lucide-react';

interface TableStatusesTabProps {
  restaurantId: number;
}

const TYPES = ['MEAL', 'TABLE', 'BOOKING'] as const;

// Faithful to the POS TableStatusForm subtype1 vocabulary per type.
const SUBTYPE1_SUGGESTIONS: Record<string, string[]> = {
  TABLE: ['NoStatus', 'Open', 'Check', 'Settlement', 'Cleaning', 'Closed'],
  BOOKING: ['NotArrived', 'Overdue', 'FullySeated', 'PartiallySeated', 'WaitingInBar'],
  MEAL: ['Drinks', 'Starters', 'Mains', 'Deserts'],
};

// POS stores colors as a packed ARGB int (Java Color.getRGB() = 0xFFRRGGBB, signed).
function argbToHex(argb?: number): string {
  if (argb === undefined || argb === null) return '#ffffff';
  return '#' + (argb & 0xffffff).toString(16).padStart(6, '0');
}
function hexToArgb(hex: string): number {
  return (0xff000000 | parseInt(hex.replace('#', ''), 16)) | 0;
}

function ColorSwatch({ rgb }: { rgb?: number }) {
  return (
    <span
      className="inline-block h-5 w-8 rounded border border-gray-300 align-middle"
      style={{ backgroundColor: argbToHex(rgb) }}
    />
  );
}

export function TableStatusesTab({ restaurantId }: TableStatusesTabProps) {
  const queryClient = useQueryClient();
  const canEdit = useHasPermission(PERM.TABLE_MONITOR);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<TableStatusDef | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: statuses = [], isLoading } = useQuery({
    queryKey: ['table-status-defs', restaurantId],
    queryFn: () => apiClient.getTableStatusDefs(restaurantId),
    enabled: !!restaurantId,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => apiClient.deleteTableStatusDef(restaurantId, uuid),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['table-status-defs', restaurantId] });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete table status');
    },
  });

  const handleDelete = async (s: TableStatusDef) => {
    if (window.confirm(`Delete table status "${s.name}"? This will be broadcast to the POS.`)) {
      try {
        await deleteMutation.mutateAsync(s.uuid);
      } catch {
        // surfaced via deleteMutation's onError -> deleteError banner
      }
    }
  };

  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['table-status-defs', restaurantId] });
    setIsModalOpen(false);
    setEditing(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading table statuses…</div>
      </div>
    );
  }

  return (
    <>
      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {statuses.length} {statuses.length === 1 ? 'status' : 'statuses'}
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditing(null); setIsModalOpen(true); }}
            className="btn btn-primary h-10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Status
          </button>
        )}
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Type', 'Subtype', 'Ask-Next Reason', 'Priority', 'Colors', 'Enabled', ''].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {statuses.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                  No table statuses defined yet.
                </td>
              </tr>
            )}
            {statuses.map((s) => (
              <tr key={s.uuid} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{s.name}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{s.type || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{s.subtype1 || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{s.subtype2 || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{s.priority ?? '—'}</td>
                <td className="px-4 py-2 text-sm">
                  <span className="inline-flex items-center gap-1">
                    <ColorSwatch rgb={s.buttonColorRgb} />
                    <span style={{ color: argbToHex(s.textColorRgb) }} className="text-xs">Aa</span>
                  </span>
                </td>
                <td className="px-4 py-2 text-sm">
                  <span className={`inline-flex rounded-full px-2 text-xs font-semibold ${s.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {s.enabled ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right text-sm">
                  {canEdit && (
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => { setEditing(s); setIsModalOpen(true); }}
                        className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={`Edit ${s.name}`}
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={`Delete ${s.name}`}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <StatusModal
          restaurantId={restaurantId}
          editing={editing}
          onClose={() => { setIsModalOpen(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </>
  );
}

interface StatusModalProps {
  restaurantId: number;
  editing: TableStatusDef | null;
  onClose: () => void;
  onSaved: () => void;
}

function StatusModal({ restaurantId, editing, onClose, onSaved }: StatusModalProps) {
  const [name, setName] = useState(editing?.name ?? '');
  const [type, setType] = useState(editing?.type ?? 'TABLE');
  const [subtype1, setSubtype1] = useState(editing?.subtype1 ?? '');
  const [subtype2, setSubtype2] = useState(editing?.subtype2 ?? '');
  const [priority, setPriority] = useState<string>(editing?.priority != null ? String(editing.priority) : '');
  const [buttonColor, setButtonColor] = useState(argbToHex(editing?.buttonColorRgb));
  const [textColor, setTextColor] = useState(argbToHex(editing?.textColorRgb ?? -16777216)); // default black
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  // Ask-Next Reasons (synced from POS) populate the subtype2 dropdown.
  const { data: askNextReasons = [] } = useQuery({
    queryKey: ['synced-ask-next-reasons', restaurantId],
    queryFn: () => apiClient.getSyncedAskNextReasons(restaurantId),
    enabled: !!restaurantId,
  });

  // Option list: enabled reasons by name, plus the current value if it's no longer present.
  const reasonOptions = Array.from(
    new Set([
      ...askNextReasons
        .filter((r: SyncedAskNextReason) => r.isEnabled !== false)
        .map((r: SyncedAskNextReason) => r.name)
        .filter(Boolean),
      ...(subtype2 ? [subtype2] : []),
    ])
  );

  // Ask-Next Reason (subtype2) is meaningful for MEAL type only — clear it otherwise.
  useEffect(() => {
    if (type !== 'MEAL') setSubtype2('');
  }, [type]);

  const save = useMutation({
    mutationFn: (body: TableStatusDefInput) =>
      editing
        ? apiClient.updateTableStatusDef(restaurantId, editing.uuid, body)
        : apiClient.createTableStatusDef(restaurantId, body),
    onSuccess: onSaved,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to save'),
  });

  const handleSubmit = () => {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    const prio = priority === '' ? undefined : Number(priority);
    if (prio !== undefined && (Number.isNaN(prio) || prio < 0)) { setError('Priority must be a non-negative number'); return; }
    save.mutate({
      name: name.trim(),
      type,
      subtype1: subtype1.trim() || undefined,
      subtype2: type === 'MEAL' ? (subtype2.trim() || undefined) : undefined,
      priority: prio,
      buttonColorRgb: hexToArgb(buttonColor),
      textColorRgb: hexToArgb(textColor),
      enabled,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h3 className="text-lg font-semibold text-gray-900">
            {editing ? 'Edit Table Status' : 'Add Table Status'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Name</span>
            <input className="input mt-1 w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Type</span>
              <select className="input mt-1 w-full" value={type} onChange={(e) => setType(e.target.value)}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Priority</span>
              <input type="number" min={0} className="input mt-1 w-full" value={priority} onChange={(e) => setPriority(e.target.value)} />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Subtype</span>
            <input
              className="input mt-1 w-full"
              list="subtype1-suggestions"
              value={subtype1}
              onChange={(e) => setSubtype1(e.target.value)}
              placeholder={type === 'MEAL' ? 'Kitchen display course' : 'State key'}
            />
            <datalist id="subtype1-suggestions">
              {(SUBTYPE1_SUGGESTIONS[type] || []).map((s) => <option key={s} value={s} />)}
            </datalist>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Ask-Next Reason</span>
            <select
              className="input mt-1 w-full disabled:bg-gray-100"
              value={subtype2}
              onChange={(e) => setSubtype2(e.target.value)}
              disabled={type !== 'MEAL'}
            >
              <option value="">{type === 'MEAL' ? '— None —' : 'Meal type only'}</option>
              {reasonOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Button color</span>
              <input type="color" className="mt-1 h-10 w-full rounded border border-gray-300" value={buttonColor} onChange={(e) => setButtonColor(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Text color</span>
              <input type="color" className="mt-1 h-10 w-full rounded border border-gray-300" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
            </label>
          </div>

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="text-sm font-medium text-gray-700">Enabled</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button onClick={onClose} className="btn h-10">Cancel</button>
          <button onClick={handleSubmit} disabled={save.isPending} className="btn btn-primary h-10">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TableStatusesTab;
